import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import type { Inquiry } from '../types/api';
import { nextStepBucket } from './nextStep';

/**
 * Issue #53 — calendar view («Календарь», Bitrix-style).
 * Deals are laid out on a month grid by their next-step date (the same
 * field that powers the «По делам» board from #52). Weeks start Monday,
 * consistent with `nextStep.ts`.
 */

export interface CalendarDay {
  date: Date;
  /** `yyyy-MM-dd` — stable key for grouping deals onto cells. */
  key: string;
  inMonth: boolean;
  isToday: boolean;
  isPast: boolean;
}

/** `yyyy-MM-dd` in local time (date-fns `format` is local by default). */
export function dayKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

/**
 * Month grid covering the calendar month of `anchor`: whole weeks
 * (Mon–Sun), padded with the neighbouring months' days so every row
 * has 7 cells.
 */
export function buildMonthGrid(anchor: Date, now: Date = new Date()): CalendarDay[][] {
  const monthStart = startOfMonth(anchor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 });
  const today = startOfDay(now).getTime();
  const weeks: CalendarDay[][] = [];
  let week: CalendarDay[] = [];
  // `addDays` (not timestamp arithmetic) keeps cells at local midnight across DST.
  for (let date = gridStart; date.getTime() <= gridEnd.getTime(); date = addDays(date, 1)) {
    week.push({
      date,
      key: dayKey(date),
      inMonth: date.getMonth() === monthStart.getMonth(),
      isToday: startOfDay(date).getTime() === today,
      isPast: startOfDay(date).getTime() < today,
    });
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  return weeks;
}

/**
 * Open deals with a valid next-step date, grouped by calendar day.
 * Closed deals (won/lost) and deals without a date are excluded — same
 * rule as `groupByNextStep` on the activity board. Undated deals surface
 * separately via `undatedCount`.
 */
export function groupDealsByDay(inquiries: Inquiry[]): Map<string, Inquiry[]> {
  const byDay = new Map<string, Inquiry[]>();
  for (const inq of inquiries) {
    if (inq.outcome !== 'NONE') continue;
    if (!inq.nextStepAt) continue;
    const day = startOfDay(new Date(inq.nextStepAt));
    if (Number.isNaN(day.getTime())) continue;
    const key = dayKey(day);
    const list = byDay.get(key);
    if (list) list.push(inq);
    else byDay.set(key, [inq]);
  }
  return byDay;
}

/** Open deals with no next-step date — shown as a hint under the grid
 *  (mirrors the «Без даты» column of the activity board). */
export function undatedCount(inquiries: Inquiry[]): number {
  return inquiries.filter((i) => i.outcome === 'NONE' && !i.nextStepAt).length;
}

/** Overdue = next-step date before today (same definition as #52). */
export function isOverdue(inquiry: Inquiry, now: Date = new Date()): boolean {
  return nextStepBucket(inquiry.nextStepAt, now) === 'overdue';
}
